import noop from 'lodash/noop';
import { fromCallback } from 'xstate';

import { Logger } from '@eluve/logger-main';

import { MuteDetector } from '../MuteDetector';
import { AudioHealthEvents } from '../machines/transcription-machine/types';
import { MuteDetectionPipeline } from '../mute-detection-pipeline';

export const validateAudioCapture = fromCallback<
  AudioHealthEvents,
  { deviceId?: string; logger: Logger },
  AudioHealthEvents
>(({ input, sendBack: unsafeSendBack }) => {
  const sendBack = unsafeSendBack as (evt: AudioHealthEvents) => void;
  const { deviceId, logger } = input;
  let mediaStream: MediaStream | null = null;
  let mutePipeline: MuteDetectionPipeline | null = null;
  let cleaningUp = false;

  const runCheck = async () => {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: deviceId ? { deviceId: { exact: deviceId } } : true,
    });

    const [audioTrack] = mediaStream.getAudioTracks();
    const selectedDeviceId = audioTrack?.getSettings().deviceId;

    if (selectedDeviceId) {
      sendBack({
        type: 'audio.audioDeviceObtained',
        deviceId: selectedDeviceId ?? '',
      });
    }

    if (audioTrack === undefined) {
      throw new Error('No audio tracks available in MediaStream');
    }

    mutePipeline = new MuteDetectionPipeline({
      MuteDetector: MuteDetector,
      mediaStream,
      onMute: noop,
      onUnmute: noop,
      onSilence: () => {
        if (!cleaningUp) {
          sendBack({ type: 'audio.silent' });
        }
      },
      onSound: () => {
        if (!cleaningUp) {
          sendBack({ type: 'audio.soundDetected' });
        }
      },
    });

    await mutePipeline.start();

    sendBack({
      type: 'audio.detectionPipelineAvailable',
      pipeline: mutePipeline,
    });
  };

  runCheck().catch((error) => {
    logger.warn(error);
  });

  return () => {
    cleaningUp = true;
    mutePipeline
      ?.stop()
      .catch((e) =>
        logger.warn('Error stopping mute detection pipeline', { error: e }),
      )
      .finally(() => {
        mediaStream?.getTracks().forEach((track) => {
          track.stop();
        });
      });
  };
});
